import { fetchRestApiEndpoint } from './utils/fetchRestApiEndpoint';
import { log } from '@/utils/logger';

type Signal = AbortSignal | null;

interface FetchJsonProps {
  url: string;
  signal?: Signal;
  headers?: Record<string, string> | null;
  options?: RequestInit;
}

const defaultHeaders = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

/**
 * Performs a GET request to fetch JSON data from a REST API endpoint with enhanced logging
 *
 * @param params - The request parameters
 * @param params.url - The endpoint URL to fetch from
 * @param params.signal - Optional AbortSignal for request cancellation
 * @param params.headers - Optional additional HTTP headers to merge with defaults
 * @param params.options - Optional additional RequestInit options to merge
 *
 * @returns Promise that resolves to the JSON response data
 *
 * @throws {Error} When the response is not ok or JSON parsing fails
 *
 * @example
 * ```typescript
 * const controller = new AbortController();
 * const result = await fetchJson({
 *   url: '/api/users/123',
 *   signal: controller.signal,
 *   headers: { 'Authorization': 'Bearer token' }
 * });
 * ```
 */
export const fetchJson = async ({
  url,
  signal = null,
  headers: additionalHeaders = {},
  options: additionalOptions = {},
}: FetchJsonProps) => {
  const mergedHeaders = { ...defaultHeaders, ...(additionalHeaders || {}) };
  const requestOptions = {
    method: 'GET',
    headers: mergedHeaders,
    signal,
    ...additionalOptions,
  };

  try {
    const response = await fetchRestApiEndpoint(url, requestOptions);

    if (!response) {
      log.throw({
        event: 'fetching BE',
        message: 'No response',
        stringyfy: { url, options: requestOptions },
      });
      return;
    }

    if (!response.ok) {
      throw new Error(response.statusText);
    }

    // Read response as text first so we can log it if JSON parsing fails
    const responseText = await response.text();

    try {
      const json: RestPayload = JSON.parse(responseText);
      return json;
    } catch (jsonError) {
      // Only log response text when JSON parsing fails
      log.throw({
        event: 'fetching BE',
        message: 'Failed to parse response as JSON',
        stringyfy: {
          url,
          status: response.status,
          contentType: response.headers.get('content-type'),
          responseText: responseText.substring(0, 1000),
          jsonError: jsonError instanceof Error ? jsonError.message : String(jsonError),
        },
      });
      throw new Error(
        `Response is not valid JSON: ${jsonError instanceof Error ? jsonError.message : String(jsonError)}`
      );
    }
  } catch (error) {
    // Re-throw JSON parsing errors as-is
    if (error instanceof Error && error.message.includes('Response is not valid JSON')) {
      throw error;
    }

    // Details for logging other errors
    log.throw({
      event: 'fetching BE',
      message: 'Error fetching JSON data',
      stringyfy: {
        url,
        options: requestOptions,
        error: error instanceof Error ? error.message : String(error),
        response: error instanceof Response ? error.statusText : 'N/A',
      },
    });

    throw new Error('Response is not JSON');
  }
};
